import React, { useState, useEffect } from 'react';
import {
    Table, Button, message, Tag, Space, Tooltip, Input, Dropdown, Menu,
    Modal, Form, Select, Card, Row, Col, DatePicker, InputNumber, Tabs,
    Descriptions, Popconfirm, Divider, Checkbox, Statistic, Badge,
    Typography, Empty, Spin, Radio
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
    EllipsisOutlined, ReloadOutlined, InboxOutlined,
    BarcodeOutlined, ExperimentOutlined, WarningOutlined,
    StockOutlined, ExclamationCircleOutlined, FilterOutlined,
    FilePdfOutlined, FileExcelOutlined, PrinterOutlined,
    ClearOutlined, DashboardOutlined
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import api from "../../components/api/api-base";
import { useBusinessContext } from '../../components/BusinessContext';
import { useAuthContext } from '../../components/AuthProvider';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const RawMaterialSupply = () => {
    const [loading, setLoading] = useState({
        supplies: false,
        suppliers: false,
        warehouses: false,
        rawMaterials: false,
        inventory: false
    });
    
    const [supplies, setSupplies] = useState([]);
    const [filteredSupplies, setFilteredSupplies] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [inventoryData, setInventoryData] = useState({
        lowStock: [],
        expiringSoon: []
    });
    const [searchText, setSearchText] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        supplierId: null,
        warehouseId: null,
        status: null,
        dateRange: null,
        materialId: null
    });
    
    // Report states
    const [reportData, setReportData] = useState([]);
    const [showReport, setShowReport] = useState(false);
    const [reportType, setReportType] = useState('supplies-by-date');
    const [reportLoading, setReportLoading] = useState(false);
    
    // Modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isInventoryModalVisible, setIsInventoryModalVisible] = useState(false);
    const [isReportModalVisible, setIsReportModalVisible] = useState(false);
    const [selectedSupply, setSelectedSupply] = useState(null);
    const [selectedMaterialForInventory, setSelectedMaterialForInventory] = useState(null);
    const [inventoryDetails, setInventoryDetails] = useState(null);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    
    // Forms
    const [form] = Form.useForm();
    const [itemsForm] = Form.useForm();
    const [reportForm] = Form.useForm();
    
    // Items state
    const [supplyItems, setSupplyItems] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);
    const [batchForm] = Form.useForm();
    
    const navigate = useNavigate();
    const { businessId, businessName } = useBusinessContext();
    const { token } = useAuthContext();

    const statusOptions = [
        'DRAFT',
        'PENDING', 
        'RECEIVED', 
        'QUARANTINED', 
        'REJECTED',
        'COMPLETED'
    ];

    const statusColors = {
        'DRAFT': 'default',
        'PENDING': 'processing',
        'RECEIVED': 'success',
        'QUARANTINED': 'warning',
        'REJECTED': 'error',
        'COMPLETED': 'success'
    };

    const unitOfMeasureOptions = [
        "kg", "g", "lb", "oz",
        "L", "mL", "gal", "fl oz",
        "m", "cm", "in", "ft",
        "piece", "dozen", "box", "roll",
        "sheet", "pack", "set", "unit"
    ];

    const reportTypes = [
        { label: 'Supplies by Date Range', value: 'supplies-by-date' },
        { label: 'Supplies by Supplier', value: 'supplies-by-supplier' },
        { label: 'Supplies by Warehouse', value: 'supplies-by-warehouse' },
        { label: 'Inventory Status', value: 'inventory-status' },
        { label: 'Expiring Batches', value: 'expiring-batches' },
        { label: 'Low Stock Items', value: 'low-stock' }
    ];

    useEffect(() => {
        if (businessId) {
            loadAllData();
        }
    }, [businessId]);

    const loadAllData = () => {
        fetchSupplies();
        fetchSuppliers();
        fetchWarehouses();
        fetchRawMaterials();
        fetchLowStockAlert();
        fetchExpiringSoonAlert();
    };

    const fetchSupplies = async () => {
        setLoading(prev => ({ ...prev, supplies: true }));
        try {
            const response = await api.get(`/rawmaterialsupplies/business/${businessId}`);
            setSupplies(response.data || []);
            applyFilters(response.data || [], filters);
        } catch (error) {
            console.error("Fetch Supplies Error:", error);
            message.error(`Failed to fetch supplies: ${error.message || 'Unknown error'}`);
            setSupplies([]);
            setFilteredSupplies([]);
        } finally {
            setLoading(prev => ({ ...prev, supplies: false }));
        }
    };

    const fetchSuppliesByWarehouse = async (warehouseId) => {
        try {
            const response = await api.get(`/rawmaterialsupplies/warehouse/${warehouseId}`);
            return response.data;
        } catch (error) {
            console.error("Fetch Supplies By Warehouse Error:", error);
            return [];
        }
    };

    const fetchSuppliesBySupplier = async (supplierId) => {
        try {
            const response = await api.get(`/rawmaterialsupplies/supplier/${supplierId}`);
            return response.data;
        } catch (error) {
            console.error("Fetch Supplies By Supplier Error:", error);
            return [];
        }
    };

    const fetchSuppliers = async () => {
        setLoading(prev => ({ ...prev, suppliers: true }));
        try {
            const response = await api.get(`/suppliers/business/${businessId}`);
            setSuppliers(response.data || []);
        } catch (error) {
            console.error("Fetch Suppliers Error:", error);
            message.error("Failed to fetch suppliers");
            setSuppliers([]);
        } finally {
            setLoading(prev => ({ ...prev, suppliers: false }));
        }
    };

    const fetchWarehouses = async () => {
        setLoading(prev => ({ ...prev, warehouses: true }));
        try {
            const response = await api.get(`/warehouses/business/${businessId}`);
            setWarehouses(response.data || []);
        } catch (error) {
            console.error("Fetch Warehouses Error:", error);
            message.error("Failed to fetch warehouses");
            setWarehouses([]);
        } finally {
            setLoading(prev => ({ ...prev, warehouses: false }));
        }
    };

    const fetchRawMaterials = async () => {
        setLoading(prev => ({ ...prev, rawMaterials: true }));
        try {
            const response = await api.get(`/rawmaterials/business/${businessId}`);
            setRawMaterials(response.data || []);
        } catch (error) {
            console.error("Fetch Raw Materials Error:", error);
            message.error("Failed to fetch raw materials");
            setRawMaterials([]);
        } finally {
            setLoading(prev => ({ ...prev, rawMaterials: false }));
        }
    };

    const fetchLowStockAlert = async () => {
        try {
            const response = await api.get(`/rawmaterialinventories/low-stock?threshold=10`);
            setInventoryData(prev => ({ ...prev, lowStock: response.data || [] }));
        } catch (error) {
            console.error("Fetch Low Stock Error:", error);
        }
    };

    const fetchExpiringSoonAlert = async () => {
        try {
            const response = await api.get(`/rawmaterialinventories/expiring-soon?daysThreshold=30`);
            setInventoryData(prev => ({ ...prev, expiringSoon: response.data || [] }));
        } catch (error) {
            console.error("Fetch Expiring Soon Error:", error);
        }
    };

    const fetchCurrentStock = async (rawMaterialId) => {
        try {
            const response = await api.get(`/rawmaterialinventories/current-stock/${rawMaterialId}`);
            return response.data;
        } catch (error) {
            console.error("Fetch Current Stock Error:", error);
            return null;
        }
    };

    const fetchMaterialDetails = async (rawMaterialId) => {
        try {
            const response = await api.get(`/rawmaterialinventories/details/${rawMaterialId}`);
            return response.data;
        } catch (error) {
            console.error("Fetch Material Details Error:", error);
            return null;
        }
    };

    const fetchWarehouseInventory = async (warehouseId) => {
        try {
            const response = await api.get(`/rawmaterialinventories/warehouse/${warehouseId}`);
            return response.data;
        } catch (error) {
            console.error("Fetch Warehouse Inventory Error:", error);
            return [];
        }
    };

    const handleViewInventory = async (record) => {
        setSelectedMaterialForInventory(record);
        setInventoryLoading(true);
        setIsInventoryModalVisible(true);
        
        try {
            const [stock, details] = await Promise.all([
                fetchCurrentStock(record.id),
                fetchMaterialDetails(record.id)
            ]);
            
            setInventoryDetails({
                stock: stock,
                details: details
            });
        } catch (error) {
            console.error("Error fetching inventory:", error);
            message.error("Failed to load inventory details");
        } finally {
            setInventoryLoading(false);
        }
    };

    const applyFilters = (data, filterValues) => {
        let filtered = [...data];
        
        if (filterValues.supplierId) {
            filtered = filtered.filter(item => item.supplierId === filterValues.supplierId);
        }
        
        if (filterValues.warehouseId) {
            filtered = filtered.filter(item => item.warehouseId === filterValues.warehouseId);
        }
        
        if (filterValues.status) {
            filtered = filtered.filter(item => item.status === filterValues.status);
        }
        
        if (filterValues.dateRange && filterValues.dateRange[0] && filterValues.dateRange[1]) {
            const startDate = filterValues.dateRange[0].startOf('day');
            const endDate = filterValues.dateRange[1].endOf('day');
            filtered = filtered.filter(item => 
                moment(item.supplyDate).isBetween(startDate, endDate, null, '[]')
            );
        }
        
        if (filterValues.materialId) {
            filtered = filtered.filter(item => 
                item.items?.some(i => i.rawMaterialId === filterValues.materialId)
            );
        }
        
        if (searchText) {
            filtered = filtered.filter(
                (supply) =>
                    supply.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                    supply.status?.toLowerCase().includes(searchText.toLowerCase()) ||
                    suppliers.find(s => s.id === supply.supplierId)?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                    warehouses.find(w => w.id === supply.warehouseId)?.name?.toLowerCase().includes(searchText.toLowerCase())
            );
        }
        
        setFilteredSupplies(filtered);
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        applyFilters(supplies, newFilters);
    };

    const clearFilters = () => {
        setFilters({
            supplierId: null,
            warehouseId: null,
            status: null,
            dateRange: null,
            materialId: null
        });
        setSearchText('');
        setFilteredSupplies(supplies);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        applyFilters(supplies, filters);
    };

    const generateReport = async () => {
        try {
            const values = await reportForm.validateFields();
            setReportLoading(true);
            
            let reportData = [];
            
            switch (values.reportType) {
                case 'supplies-by-date':
                    const startDate = values.dateRange[0].toISOString();
                    const endDate = values.dateRange[1].toISOString();
                    const suppliesResponse = await api.get(`/rawmaterialsupplies/business/${businessId}`);
                    reportData = suppliesResponse.data.filter(item => 
                        moment(item.supplyDate).isBetween(values.dateRange[0], values.dateRange[1], null, '[]')
                    ) || [];
                    break;
                    
                case 'supplies-by-supplier':
                    if (values.supplierId) {
                        reportData = await fetchSuppliesBySupplier(values.supplierId);
                    }
                    break;
                    
                case 'supplies-by-warehouse':
                    if (values.warehouseId) {
                        reportData = await fetchSuppliesByWarehouse(values.warehouseId);
                    }
                    break;
                    
                case 'inventory-status':
                    const inventoryPromises = rawMaterials.map(async (material) => {
                        const stock = await fetchCurrentStock(material.id);
                        return {
                            materialCode: material.code,
                            materialName: material.name,
                            currentStock: stock || 0,
                            uom: material.baseUnitOfMeasure,
                            estimatedCost: material.estimatedUnitCost,
                            totalValue: (stock || 0) * (material.estimatedUnitCost || 0)
                        };
                    });
                    reportData = await Promise.all(inventoryPromises);
                    break;
                    
                case 'expiring-batches':
                    const expiringResponse = await api.get(`/rawmaterialinventories/expiring-soon?daysThreshold=${values.expiryDays || 30}`);
                    reportData = expiringResponse.data || [];
                    break;
                    
                case 'low-stock':
                    const lowStockResponse = await api.get(`/rawmaterialinventories/low-stock?threshold=${values.stockThreshold || 10}`);
                    reportData = lowStockResponse.data || [];
                    break;
            }
            
            setReportData(reportData);
            setReportType(values.reportType);
            setShowReport(true);
            
        } catch (error) {
            console.error("Report Generation Error:", error);
            message.error("Failed to generate report");
        } finally {
            setReportLoading(false);
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF();
            
            doc.setFontSize(16);
            doc.text(`Raw Material Report - ${businessName}`, 14, 15);
            doc.setFontSize(10);
            doc.text(`Generated: ${moment().format('YYYY-MM-DD HH:mm')}`, 14, 22);
            doc.text(`Report Type: ${reportTypes.find(r => r.value === reportType)?.label || reportType}`, 14, 29);
            
            const tableColumn = getReportColumns();
            const tableRows = getReportRows(reportData, reportType);
            
            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 35,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [24, 144, 255] }
            });
            
            doc.save(`raw-material-report-${moment().format('YYYYMMDD-HHmm')}.pdf`);
            message.success("PDF exported successfully");
        } catch (error) {
            console.error("PDF Export Error:", error);
            message.error("Failed to export PDF");
        }
    };

    const exportToExcel = () => {
        try {
            const worksheet = XLSX.utils.json_to_sheet(formatReportDataForExcel(reportData, reportType));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
            XLSX.writeFile(workbook, `raw-material-report-${moment().format('YYYYMMDD-HHmm')}.xlsx`);
            message.success("Excel exported successfully");
        } catch (error) {
            console.error("Excel Export Error:", error);
            message.error("Failed to export Excel");
        }
    };

    const getReportColumns = () => {
        switch (reportType) {
            case 'supplies-by-date':
            case 'supplies-by-supplier':
            case 'supplies-by-warehouse':
                return ['Date', 'Supplier', 'Warehouse', 'Items', 'Total Qty', 'Status'];
            case 'inventory-status':
                return ['Material Code', 'Material Name', 'Current Stock', 'UOM', 'Unit Cost', 'Total Value'];
            case 'expiring-batches':
                return ['Batch Number', 'Material', 'Quantity', 'UOM', 'Expiry Date', 'Days Left'];
            case 'low-stock':
                return ['Material Code', 'Material Name', 'Current Stock', 'UOM'];
            default:
                return [];
        }
    };

    const getReportRows = (data, type) => {
        return data.map(item => {
            switch (type) {
                case 'supplies-by-date':
                case 'supplies-by-supplier':
                case 'supplies-by-warehouse':
                    return [
                        moment(item.supplyDate).format('YYYY-MM-DD'),
                        suppliers.find(s => s.id === item.supplierId)?.name || '-',
                        warehouses.find(w => w.id === item.warehouseId)?.name || '-',
                        item.items?.length || 0,
                        getTotalItems(item).toFixed(2),
                        item.status
                    ];
                case 'inventory-status':
                    return [
                        item.materialCode,
                        item.materialName,
                        item.currentStock?.toFixed(2),
                        item.uom,
                        `₵${item.estimatedCost?.toFixed(2) || '0.00'}`,
                        `₵${item.totalValue?.toFixed(2) || '0.00'}`
                    ];
                case 'expiring-batches':
                    return [
                        item.batchNumber,
                        item.materialName,
                        item.quantity?.toFixed(2),
                        item.uom,
                        moment(item.expiryDate).format('YYYY-MM-DD'),
                        moment(item.expiryDate).diff(moment(), 'days')
                    ];
                case 'low-stock':
                    return [
                        item.materialCode,
                        item.materialName,
                        item.currentStock?.toFixed(2),
                        item.uom
                    ];
                default:
                    return [];
            }
        });
    };

    const formatReportDataForExcel = (data, type) => {
        return data.map(item => {
            switch (type) {
                case 'supplies-by-date':
                case 'supplies-by-supplier':
                case 'supplies-by-warehouse':
                    return {
                        'Date': moment(item.supplyDate).format('YYYY-MM-DD'),
                        'Supplier': suppliers.find(s => s.id === item.supplierId)?.name || '-',
                        'Warehouse': warehouses.find(w => w.id === item.warehouseId)?.name || '-',
                        'Items': item.items?.length || 0,
                        'Total Quantity': getTotalItems(item).toFixed(2),
                        'Status': item.status
                    };
                case 'inventory-status':
                    return {
                        'Material Code': item.materialCode,
                        'Material Name': item.materialName,
                        'Current Stock': item.currentStock?.toFixed(2),
                        'UOM': item.uom,
                        'Unit Cost': item.estimatedCost,
                        'Total Value': item.totalValue?.toFixed(2)
                    };
                case 'expiring-batches':
                    return {
                        'Batch Number': item.batchNumber,
                        'Material': item.materialName,
                        'Quantity': item.quantity?.toFixed(2),
                        'UOM': item.uom,
                        'Expiry Date': moment(item.expiryDate).format('YYYY-MM-DD'),
                        'Days Left': moment(item.expiryDate).diff(moment(), 'days')
                    };
                case 'low-stock':
                    return {
                        'Material Code': item.materialCode,
                        'Material Name': item.materialName,
                        'Current Stock': item.currentStock?.toFixed(2),
                        'UOM': item.uom
                    };
                default:
                    return item;
            }
        });
    };

    const printReport = () => {
        const printWindow = window.open('', '_blank');
        const columns = getReportColumns();
        const rows = getReportRows(reportData, reportType);
        
        let html = `
            <html>
            <head>
                <title>Raw Material Report - ${businessName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #1890ff; }
                    .header { margin-bottom: 20px; }
                    table { border-collapse: collapse; width: 100%; }
                    th { background-color: #1890ff; color: white; padding: 8px; text-align: left; }
                    td { border: 1px solid #ddd; padding: 8px; }
                    tr:nth-child(even) { background-color: #f2f2f2; }
                    .footer { margin-top: 20px; font-size: 12px; color: #666; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Raw Material Report - ${businessName}</h1>
                    <p>Generated: ${moment().format('YYYY-MM-DD HH:mm')}</p>
                    <p>Report Type: ${reportTypes.find(r => r.value === reportType)?.label || reportType}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            ${columns.map(col => `<th>${col}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map(row => `
                            <tr>
                                ${row.map(cell => `<td>${cell}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="footer">
                    <p>Total Records: ${reportData.length}</p>
                </div>
            </body>
            </html>
        `;
        
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handleAdd = () => {
        setSelectedSupply(null);
        setSupplyItems([]);
        form.resetFields();
        itemsForm.resetFields();
        form.setFieldsValue({
            status: 'DRAFT',
            supplyDate: moment()
        });
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setSelectedSupply(record);
        setSupplyItems(record.items || []);
        form.setFieldsValue({
            warehouseId: record.warehouseId,
            supplierId: record.supplierId,
            description: record.description,
            supplyDate: record.supplyDate ? moment(record.supplyDate) : null,
            status: record.status
        });
        setIsModalVisible(true);
    };

    const handleView = (record) => {
        setSelectedSupply(record);
        setIsViewModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/rawmaterialsupplies/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success("Supply deleted successfully!");
            fetchSupplies();
            fetchLowStockAlert();
            fetchExpiringSoonAlert();
        } catch (error) {
            console.error("Delete Error:", error);
            message.error(`Failed to delete: ${error.message || 'Unknown error'}`);
        }
    };

    const handleAddItem = () => {
        try {
            itemsForm.validateFields().then(values => {
                const newItem = {
                    id: `temp-${Date.now()}`,
                    rawMaterialId: values.rawMaterialId,
                    quantity: values.quantity,
                    unitOfMeasure: values.unitOfMeasure,
                    batches: []
                };
                
                const material = rawMaterials.find(m => m.id === values.rawMaterialId);
                if (material) {
                    newItem.materialName = material.name;
                    newItem.materialCode = material.code;
                }
                
                setSupplyItems([...supplyItems, newItem]);
                itemsForm.resetFields();
                message.success("Item added successfully!");
            });
        } catch (error) {
            console.error("Add Item Error:", error);
        }
    };

    const handleRemoveItem = (index) => {
        const newItems = [...supplyItems];
        newItems.splice(index, 1);
        setSupplyItems(newItems);
    };

    const handleManageBatches = (index) => {
        setCurrentItemIndex(index);
        setSelectedMaterial(supplyItems[index]);
        setIsBatchModalVisible(true);
    };

    const handleAddBatch = () => {
        try {
            batchForm.validateFields().then(values => {
                const newBatch = {
                    id: `temp-${Date.now()}`,
                    batchNumber: values.batchNumber,
                    quantity: values.quantity,
                    unitOfMeasure: values.unitOfMeasure,
                    manufactureDate: values.manufactureDate ? values.manufactureDate.toISOString() : null,
                    expiryDate: values.expiryDate ? values.expiryDate.toISOString() : null,
                    isQuarantined: values.isQuarantined || false,
                    quarantineReason: values.quarantineReason || ''
                };
                
                const updatedItems = [...supplyItems];
                if (!updatedItems[currentItemIndex].batches) {
                    updatedItems[currentItemIndex].batches = [];
                }
                updatedItems[currentItemIndex].batches.push(newBatch);
                setSupplyItems(updatedItems);
                
                batchForm.resetFields();
                message.success("Batch added successfully!");
            });
        } catch (error) {
            console.error("Add Batch Error:", error);
        }
    };

    const handleRemoveBatch = (batchIndex) => {
        const updatedItems = [...supplyItems];
        updatedItems[currentItemIndex].batches.splice(batchIndex, 1);
        setSupplyItems(updatedItems);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(prev => ({ ...prev, supplies: true }));

            if (supplyItems.length === 0) {
                message.error("Please add at least one item to the supply");
                setLoading(prev => ({ ...prev, supplies: false }));
                return;
            }

            const payload = {
                businessId: businessId,
                warehouseId: values.warehouseId,
                supplierId: values.supplierId,
                description: values.description || '',
                supplyDate: values.supplyDate.toISOString(),
                status: values.status,
                items: supplyItems.map(item => ({
                    id: item.id?.startsWith('temp-') ? null : item.id,
                    rawMaterialId: item.rawMaterialId,
                    quantity: parseFloat(item.quantity),
                    unitOfMeasure: item.unitOfMeasure,
                    batches: (item.batches || []).map(batch => ({
                        id: batch.id?.startsWith('temp-') ? null : batch.id,
                        batchNumber: batch.batchNumber,
                        quantity: parseFloat(batch.quantity),
                        unitOfMeasure: batch.unitOfMeasure,
                        manufactureDate: batch.manufactureDate,
                        expiryDate: batch.expiryDate,
                        isQuarantined: batch.isQuarantined || false,
                        quarantineReason: batch.quarantineReason || ''
                    }))
                }))
            };

            if (selectedSupply) {
                await api.put(`/rawmaterialsupplies/${selectedSupply.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Supply updated successfully!");
            } else {
                await api.post(`/rawmaterialsupplies`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Supply created successfully!");
            }

            setIsModalVisible(false);
            form.resetFields();
            setSelectedSupply(null);
            setSupplyItems([]);
            loadAllData();
        } catch (error) {
            console.error("Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(prev => ({ ...prev, supplies: false }));
        }
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setSelectedSupply(null);
        setSupplyItems([]);
    };

    const getTotalItems = (supply) => {
        return supply.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
    };

    const getTotalBatches = (supply) => {
        return supply.items?.reduce((sum, item) => sum + (item.batches?.length || 0), 0) || 0;
    };

    const columns = [
        {
            title: 'Supply Date',
            dataIndex: 'supplyDate',
            key: 'supplyDate',
            width: 120,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
            sorter: (a, b) => moment(a.supplyDate).unix() - moment(b.supplyDate).unix()
        },
        {
            title: 'Supplier',
            dataIndex: 'supplierId',
            key: 'supplier',
            render: (id) => {
                const supplier = suppliers.find(s => s.id === id);
                return supplier ? supplier.name : '-';
            },
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouseId',
            key: 'warehouse',
            render: (id) => {
                const warehouse = warehouses.find(w => w.id === id);
                return warehouse ? warehouse.name : '-';
            },
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => text && text.length > 50 ? text.substring(0, 50) + '...' : text || '-',
        },
        {
            title: 'Items',
            key: 'items',
            render: (_, record) => (
                <Tooltip title={`${record.items?.length || 0} items, ${getTotalBatches(record)} batches`}>
                    <Tag icon={<InboxOutlined />}>
                        {record.items?.length || 0} items
                    </Tag>
                </Tooltip>
            ),
        },
        {
            title: 'Total Qty',
            key: 'totalQty',
            render: (_, record) => getTotalItems(record).toFixed(2),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColors[status] || 'default'}>
                    {status || 'DRAFT'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<StockOutlined />}
                        onClick={() => handleViewInventory(record)}
                        title="View Inventory"
                    />
                    <Dropdown
                        overlay={
                            <Menu>
                                <Menu.Item key="view" onClick={() => handleView(record)}>
                                    <EyeOutlined /> View Details
                                </Menu.Item>
                                <Menu.Item key="edit" onClick={() => handleEdit(record)}>
                                    <EditOutlined /> Edit
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item key="delete" danger>
                                    <Popconfirm
                                        title="Are you sure you want to delete this supply?"
                                        onConfirm={() => handleDelete(record.id)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <DeleteOutlined /> Delete
                                    </Popconfirm>
                                </Menu.Item>
                            </Menu>
                        }
                        trigger={["click"]}
                    >
                        <Button icon={<EllipsisOutlined />} size="small" />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    const supplyColumns = [
        {
            title: 'Material',
            dataIndex: 'rawMaterialId',
            key: 'material',
            render: (id) => {
                const material = rawMaterials.find(m => m.id === id);
                return material ? `${material.code} - ${material.name}` : '-';
            },
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (text) => text.toFixed(2),
        },
        {
            title: 'UOM',
            dataIndex: 'unitOfMeasure',
            key: 'unitOfMeasure',
        },
        {
            title: 'Batches',
            key: 'batches',
            render: (_, record) => (
                <span>
                    {record.batches?.length || 0} batches
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record, index) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<ExperimentOutlined />}
                        onClick={() => handleManageBatches(index)}
                    >
                        Manage Batches
                    </Button>
                    <Button 
                        size="small" 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveItem(index)}
                    />
                </Space>
            ),
        },
    ];

    const batchColumns = [
        {
            title: 'Batch Number',
            dataIndex: 'batchNumber',
            key: 'batchNumber',
            render: (text) => <Tag icon={<BarcodeOutlined />}>{text}</Tag>,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (text) => text.toFixed(2),
        },
        {
            title: 'UOM',
            dataIndex: 'unitOfMeasure',
            key: 'unitOfMeasure',
        },
        {
            title: 'Manufacture Date',
            dataIndex: 'manufactureDate',
            key: 'manufactureDate',
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                record.isQuarantined ? 
                    <Tag color="warning" icon={<ExclamationCircleOutlined />}>Quarantined</Tag> :
                    <Tag color="success">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, __, index) => (
                <Button 
                    size="small" 
                    danger 
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveBatch(index)}
                />
            ),
        },
    ];

    return (
        <div className="container-fluid mt-4">
            {/* Inventory Alerts */}
            <Row gutter={16} className="mb-3">
                <Col span={8}>
                    <Card size="small" hoverable>
                        <Statistic
                            title="Low Stock Items"
                            value={inventoryData.lowStock?.length || 0}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<WarningOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" hoverable>
                        <Statistic
                            title="Expiring Soon (30 days)"
                            value={inventoryData.expiringSoon?.length || 0}
                            valueStyle={{ color: '#faad14' }}
                            prefix={<ExclamationCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card 
                        size="small" 
                        hoverable
                        onClick={() => setIsReportModalVisible(true)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Statistic
                            title="Generate Report"
                            value="Click to generate"
                            valueStyle={{ color: '#1890ff', fontSize: '14px' }}
                            prefix={<FilePdfOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main Card */}
            <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                    <Row align="middle" justify="space-between">
                        <Col>
                            <h4 className="mb-0">
                                Raw Material Supplies - <span style={{ color: '#1890ff' }}>{businessName}</span>
                            </h4>
                        </Col>
                        <Col>
                            <Space>
                                <Button 
                                    icon={<DashboardOutlined />} 
                                    onClick={() => setIsReportModalVisible(true)}
                                >
                                    Reports
                                </Button>
                                <Button 
                                    icon={<ReloadOutlined />} 
                                    onClick={loadAllData}
                                    loading={loading.supplies}
                                >
                                    Refresh
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />} 
                                    onClick={handleAdd}
                                >
                                    New Supply
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div className="card-body">
                    {/* Filters */}
                    <Card size="small" className="mb-3">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Input.Search
                                        placeholder="Search supplies..."
                                        value={searchText}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        allowClear
                                    />
                                </Col>
                                <Col span={4}>
                                    <Select
                                        placeholder="Filter by Supplier"
                                        value={filters.supplierId}
                                        onChange={(value) => handleFilterChange('supplierId', value)}
                                        allowClear
                                        style={{ width: '100%' }}
                                        loading={loading.suppliers}
                                    >
                                        {suppliers.map(s => (
                                            <Option key={s.id} value={s.id}>{s.name}</Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={4}>
                                    <Select
                                        placeholder="Filter by Warehouse"
                                        value={filters.warehouseId}
                                        onChange={(value) => handleFilterChange('warehouseId', value)}
                                        allowClear
                                        style={{ width: '100%' }}
                                        loading={loading.warehouses}
                                    >
                                        {warehouses.map(w => (
                                            <Option key={w.id} value={w.id}>{w.name}</Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={4}>
                                    <Select
                                        placeholder="Filter by Status"
                                        value={filters.status}
                                        onChange={(value) => handleFilterChange('status', value)}
                                        allowClear
                                        style={{ width: '100%' }}
                                    >
                                        {statusOptions.map(s => (
                                            <Option key={s} value={s}>
                                                <Tag color={statusColors[s]}>{s}</Tag>
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={6}>
                                    <RangePicker
                                        placeholder={['Start Date', 'End Date']}
                                        value={filters.dateRange}
                                        onChange={(dates) => handleFilterChange('dateRange', dates)}
                                        style={{ width: '100%' }}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Select
                                        placeholder="Filter by Material"
                                        value={filters.materialId}
                                        onChange={(value) => handleFilterChange('materialId', value)}
                                        allowClear
                                        style={{ width: '100%' }}
                                        loading={loading.rawMaterials}
                                    >
                                        {rawMaterials.map(m => (
                                            <Option key={m.id} value={m.id}>
                                                {m.code} - {m.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={18}>
                                    <Space>
                                        <Badge count={Object.values(filters).filter(v => v !== null && v !== undefined && v !== '').length}>
                                            <Button icon={<FilterOutlined />}>Active Filters</Button>
                                        </Badge>
                                        <Button icon={<ClearOutlined />} onClick={clearFilters}>
                                            Clear All
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                    </Card>

                    {/* Table */}
                    <Table
                        columns={columns}
                        dataSource={filteredSupplies}
                        rowKey="id"
                        loading={loading.supplies}
                        pagination={{ 
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} supplies`
                        }}
                        bordered
                        scroll={{ x: 1400 }}
                        size="middle"
                    />
                </div>
            </div>

            {/* Add/Edit Supply Modal */}
            <Modal
                title={selectedSupply ? "Edit Supply" : "New Raw Material Supply"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={selectedSupply ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading.supplies}
                width={900}
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
            >
                <Tabs defaultActiveKey="1">
                    <TabPane tab="Supply Details" key="1">
                        <Form form={form} layout="vertical">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Supplier"
                                        name="supplierId"
                                        rules={[{ required: true, message: 'Please select supplier!' }]}
                                    >
                                        <Select 
                                            placeholder="Select supplier"
                                            loading={loading.suppliers}
                                        >
                                            {suppliers.map(supplier => (
                                                <Option key={supplier.id} value={supplier.id}>
                                                    {supplier.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Warehouse"
                                        name="warehouseId"
                                        rules={[{ required: true, message: 'Please select warehouse!' }]}
                                    >
                                        <Select 
                                            placeholder="Select warehouse"
                                            loading={loading.warehouses}
                                        >
                                            {warehouses.map(warehouse => (
                                                <Option key={warehouse.id} value={warehouse.id}>
                                                    {warehouse.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Supply Date"
                                        name="supplyDate"
                                        rules={[{ required: true, message: 'Please select supply date!' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Status"
                                        name="status"
                                        rules={[{ required: true, message: 'Please select status!' }]}
                                    >
                                        <Select placeholder="Select status">
                                            {statusOptions.map(status => (
                                                <Option key={status} value={status}>
                                                    <Tag color={statusColors[status]}>{status}</Tag>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Description"
                                name="description"
                            >
                                <TextArea placeholder="Enter description" rows={3} />
                            </Form.Item>
                        </Form>
                    </TabPane>

                    <TabPane tab="Supply Items" key="2">
                        <Card size="small" title="Add Items" className="mb-3">
                            <Form form={itemsForm} layout="inline">
                                <Form.Item
                                    name="rawMaterialId"
                                    rules={[{ required: true, message: 'Select material' }]}
                                    style={{ width: 250 }}
                                >
                                    <Select 
                                        placeholder="Select raw material"
                                        loading={loading.rawMaterials}
                                    >
                                        {rawMaterials.map(material => (
                                            <Option key={material.id} value={material.id}>
                                                {material.code} - {material.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name="quantity"
                                    rules={[{ required: true, message: 'Enter quantity' }]}
                                >
                                    <InputNumber placeholder="Quantity" min={0} step={0.001} />
                                </Form.Item>
                                <Form.Item
                                    name="unitOfMeasure"
                                    rules={[{ required: true, message: 'Select UOM' }]}
                                    style={{ width: 120 }}
                                >
                                    <Select placeholder="UOM">
                                        {unitOfMeasureOptions.map(uom => (
                                            <Option key={uom} value={uom}>{uom}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                                        Add Item
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>

                        <Table
                            columns={supplyColumns}
                            dataSource={supplyItems}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </TabPane>
                </Tabs>
            </Modal>

            {/* Batch Management Modal */}
            <Modal
                title={`Manage Batches - ${selectedMaterial?.materialCode || ''} ${selectedMaterial?.materialName || ''}`}
                open={isBatchModalVisible}
                onCancel={() => setIsBatchModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsBatchModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={900}
            >
                <Card size="small" title="Add Batch" className="mb-3">
                    <Form form={batchForm} layout="vertical">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Batch Number"
                                    name="batchNumber"
                                    rules={[{ required: true, message: 'Enter batch number' }]}
                                >
                                    <Input placeholder="e.g., BATCH-001" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Quantity"
                                    name="quantity"
                                    rules={[{ required: true, message: 'Enter quantity' }]}
                                >
                                    <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="UOM"
                                    name="unitOfMeasure"
                                    rules={[{ required: true, message: 'Select UOM' }]}
                                >
                                    <Select placeholder="Select UOM">
                                        {unitOfMeasureOptions.map(uom => (
                                            <Option key={uom} value={uom}>{uom}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Manufacture Date"
                                    name="manufactureDate"
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Expiry Date"
                                    name="expiryDate"
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="isQuarantined"
                                    valuePropName="checked"
                                >
                                    <Checkbox>Quarantine this batch</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item
                                    name="quarantineReason"
                                >
                                    <Input placeholder="Quarantine reason (if applicable)" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddBatch}>
                                Add Batch
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

                <Table
                    columns={batchColumns}
                    dataSource={selectedMaterial?.batches || []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    bordered
                />
            </Modal>

            {/* View Details Modal */}
            <Modal
                title="Supply Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedSupply && (
                    <div>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Supply Date">
                                {moment(selectedSupply.supplyDate).format('YYYY-MM-DD HH:mm')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={statusColors[selectedSupply.status]}>
                                    {selectedSupply.status}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Supplier">
                                {suppliers.find(s => s.id === selectedSupply.supplierId)?.name || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Warehouse">
                                {warehouses.find(w => w.id === selectedSupply.warehouseId)?.name || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Description" span={2}>
                                {selectedSupply.description || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">Items</Divider>
                        
                        {selectedSupply.items?.map((item, index) => {
                            const material = rawMaterials.find(m => m.id === item.rawMaterialId);
                            return (
                                <Card key={index} size="small" className="mb-2">
                                    <Descriptions column={3} size="small">
                                        <Descriptions.Item label="Material">
                                            {material ? `${material.code} - ${material.name}` : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Quantity">
                                            {item.quantity} {item.unitOfMeasure}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Batches">
                                            {item.batches?.length || 0}
                                        </Descriptions.Item>
                                    </Descriptions>
                                    
                                    {item.batches?.length > 0 && (
                                        <Table
                                            columns={[
                                                { title: 'Batch', dataIndex: 'batchNumber' },
                                                { title: 'Quantity', dataIndex: 'quantity' },
                                                { title: 'UOM', dataIndex: 'unitOfMeasure' },
                                                { 
                                                    title: 'Expiry', 
                                                    dataIndex: 'expiryDate',
                                                    render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-'
                                                },
                                                {
                                                    title: 'Status',
                                                    render: (_, record) => record.isQuarantined ? 
                                                        <Tag color="warning">Quarantined</Tag> : 
                                                        <Tag color="success">Active</Tag>
                                                }
                                            ]}
                                            dataSource={item.batches}
                                            rowKey="id"
                                            pagination={false}
                                            size="small"
                                        />
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </Modal>

            {/* Inventory Details Modal */}
            <Modal
                title={`Inventory Details - ${selectedMaterialForInventory?.name || ''}`}
                open={isInventoryModalVisible}
                onCancel={() => {
                    setIsInventoryModalVisible(false);
                    setInventoryDetails(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setIsInventoryModalVisible(false);
                        setInventoryDetails(null);
                    }}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                <Spin spinning={inventoryLoading}>
                    {inventoryDetails && (
                        <div>
                            <Row gutter={16} className="mb-3">
                                <Col span={12}>
                                    <Card>
                                        <Statistic
                                            title="Current Stock"
                                            value={inventoryDetails.stock || 0}
                                            precision={2}
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card>
                                        <Statistic
                                            title="Unit of Measure"
                                            value={selectedMaterialForInventory?.baseUnitOfMeasure || '-'}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                            
                            <Divider orientation="left">Detailed Information</Divider>
                            
                            {inventoryDetails.details ? (
                                <Descriptions bordered column={1}>
                                    <Descriptions.Item label="Material Code">
                                        {selectedMaterialForInventory?.code}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Material Name">
                                        {selectedMaterialForInventory?.name}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Description">
                                        {selectedMaterialForInventory?.description || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Total Quantity">
                                        {inventoryDetails.details.totalQuantity || 0} {selectedMaterialForInventory?.baseUnitOfMeasure}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Available Quantity">
                                        {inventoryDetails.details.availableQuantity || 0} {selectedMaterialForInventory?.baseUnitOfMeasure}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Reserved Quantity">
                                        {inventoryDetails.details.reservedQuantity || 0} {selectedMaterialForInventory?.baseUnitOfMeasure}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Quarantined Quantity">
                                        {inventoryDetails.details.quarantinedQuantity || 0} {selectedMaterialForInventory?.baseUnitOfMeasure}
                                    </Descriptions.Item>
                                </Descriptions>
                            ) : (
                                <Empty description="No detailed inventory information available" />
                            )}
                        </div>
                    )}
                </Spin>
            </Modal>

            {/* Report Generation Modal */}
            <Modal
                title="Generate Report"
                open={isReportModalVisible}
                onCancel={() => {
                    setIsReportModalVisible(false);
                    setShowReport(false);
                    reportForm.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form form={reportForm} layout="vertical" onFinish={generateReport}>
                    <Form.Item
                        name="reportType"
                        label="Report Type"
                        rules={[{ required: true, message: 'Please select report type' }]}
                        initialValue="supplies-by-date"
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                {reportTypes.map(type => (
                                    <Radio key={type.value} value={type.value}>{type.label}</Radio>
                                ))}
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.reportType !== currentValues.reportType}
                    >
                        {({ getFieldValue }) => {
                            const reportType = getFieldValue('reportType');
                            
                            return (
                                <>
                                    {reportType === 'supplies-by-date' && (
                                        <Form.Item
                                            name="dateRange"
                                            label="Date Range"
                                            rules={[{ required: true, message: 'Please select date range' }]}
                                        >
                                            <RangePicker style={{ width: '100%' }} />
                                        </Form.Item>
                                    )}

                                    {reportType === 'supplies-by-supplier' && (
                                        <Form.Item
                                            name="supplierId"
                                            label="Supplier"
                                            rules={[{ required: true, message: 'Please select supplier' }]}
                                        >
                                            <Select placeholder="Select supplier">
                                                {suppliers.map(s => (
                                                    <Option key={s.id} value={s.id}>{s.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    {reportType === 'supplies-by-warehouse' && (
                                        <Form.Item
                                            name="warehouseId"
                                            label="Warehouse"
                                            rules={[{ required: true, message: 'Please select warehouse' }]}
                                        >
                                            <Select placeholder="Select warehouse">
                                                {warehouses.map(w => (
                                                    <Option key={w.id} value={w.id}>{w.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    {reportType === 'expiring-batches' && (
                                        <Form.Item
                                            name="expiryDays"
                                            label="Days Threshold"
                                            initialValue={30}
                                            rules={[{ required: true, message: 'Please enter days threshold' }]}
                                        >
                                            <InputNumber min={1} max={365} style={{ width: '100%' }} />
                                        </Form.Item>
                                    )}

                                    {reportType === 'low-stock' && (
                                        <Form.Item
                                            name="stockThreshold"
                                            label="Stock Threshold"
                                            initialValue={10}
                                            rules={[{ required: true, message: 'Please enter stock threshold' }]}
                                        >
                                            <InputNumber min={1} style={{ width: '100%' }} />
                                        </Form.Item>
                                    )}
                                </>
                            );
                        }}
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={reportLoading}>
                                Generate Report
                            </Button>
                            <Button onClick={() => setIsReportModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                {showReport && reportData.length > 0 && (
                    <div className="mt-4">
                        <Divider>Report Results</Divider>
                        <Space className="mb-3">
                            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
                                Export PDF
                            </Button>
                            <Button icon={<FileExcelOutlined />} onClick={exportToExcel}>
                                Export Excel
                            </Button>
                            <Button icon={<PrinterOutlined />} onClick={printReport}>
                                Print
                            </Button>
                        </Space>
                        
                        <Table
                            columns={getReportColumns().map(col => ({ title: col, dataIndex: col }))}
                            dataSource={formatReportDataForExcel(reportData, reportType)}
                            pagination={{ pageSize: 5 }}
                            size="small"
                            scroll={{ x: 'max-content' }}
                        />
                        
                        <Text type="secondary">Total Records: {reportData.length}</Text>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RawMaterialSupply;